import React, { useState, useRef, useEffect } from "react";
import _ from "lodash";
import { Map, Marker, Popup } from "react-leaflet";
import { of } from "rxjs";
import L from "leaflet";
import { MarkerIcon } from "./MarkerIcon";
import AwesomeTileLayer from "./awesome.tilelayer";
import MarkerClusterGroup from "react-leaflet-markercluster";
import LoadingControl from "./loading.control";
import { calculatePanelHeight } from "components/OverviewPages/Map/size";
import { delay } from "rxjs/operators";
import { generateGuid } from "core/utils/str";
import PropTypes from "prop-types";
import mapStyle from "assets/jss/default/components/mapStyle";
import makeStyles from "@mui/styles/makeStyles";
import { useEffectOnce, useUnmount } from "react-use";

// css
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "react-leaflet-markercluster/dist/styles.min.css";

const useStyles = makeStyles(mapStyle);

function MapControl(props) {
  const [controlHeight, setControlHeight] = useState(
    "calc(100vh - 8rem - 123px)" // = screen height - KPI height - top bar height
  );
  const [invalidationSubscription, setInvalidationSubscription] = useState();
  useUnmount(() => {
    if (invalidationSubscription) {
      invalidationSubscription.unsubscribe();
    }
  });
  const mapRef = useRef();
  const awesomeTileLayerRef = useRef();
  const [mapZoom, setMapZoom] = useState(props.startingZoom);
  function getPanelId() {
    return _.get(props.panel, "id", generateGuid());
  }

  function calculateHeight() {
    const panelId = getPanelId();
    if (panelId === null) {
      return "auto";
    }

    return calculatePanelHeight(panelId);
  }

  function setControlHeightInner() {
    const height = calculateHeight();

    if (_.isFinite(height) && Number(height) > 0) {
      const heightNumber = parseInt(height, 10);
      setControlHeight(heightNumber - 15);
    }
  }

  function invalidateMapSize() {
    if (!mapRef.current) {
      return;
    }
    const map = mapRef.current.leafletElement;
    const layer = awesomeTileLayerRef.current.leafletElement;
    setInvalidationSubscription(
      of(null)
        .pipe(delay(400))
        .subscribe(() => {
          map.invalidateSize();
          layer.redraw();
        })
    );
  }

  function getFallbackMarker() {
    return (
      <Marker
        position={props.centerPosition}
        key={`${_.get(props.panel, "id", generateGuid())}_fallback1`}
        icon={new MarkerIcon({ rgbColor: [255, 255, 255], opacity: 1.0 })}
      >
        <Popup>Fallback marker</Popup>
      </Marker>
    );
  }

  function renderMarkers(markers) {
    return markers.map(marker => {
      const markerRef = React.createRef();
      const resolvedMarker = new Promise(resolve => {
        (function waitForValue() {
          if (markerRef.current) return resolve(markerRef.current);
          setTimeout(waitForValue, 300);
        })();
      });
      resolvedMarker.then(resolvedMarkerRef => {
        if (!resolvedMarkerRef) {
          return;
        }
        resolvedMarkerRef.leafletElement.on("click", e => {
          if (marker.onClick) {
            if (mapRef.current.viewport.zoom < 4) {
              setMapZoom(4);
            }
            marker.onClick(e);
          }
        });
      });
      return (
        <Marker
          ref={markerRef}
          position={[marker.latitude, marker.longitude]}
          key={`${getPanelId()}_${marker.name}`}
          icon={marker.icon(markerRef)}
          interactive={marker.interactive}
          zIndexOffset={marker.zIndexOffset ? marker.zIndexOffset : 0}
          bubblingMouseEvents={!!marker.bubblingMouseEvents}
        >
          {marker.hasCustomPopup ? (
            marker.customPopup
          ) : !marker.noTooltip ? (
            <Popup>
              {marker.label}: {marker.valueToDisplay}
            </Popup>
          ) : null}
        </Marker>
      );
    });
  }

  function renderMarkerGroups(markers, clusterMarkers) {
    if (markers && markers.length > 0) {
      if (clusterMarkers) {
        return (
          <MarkerClusterGroup
            disableClusteringAtZoom={props.disableClusteringAtZoom}
            spiderfyOnMaxZoom={props.spiderfyOnMaxZoom}
          >
            {renderMarkers(markers)}
          </MarkerClusterGroup>
        );
      } else {
        return renderMarkers(markers);
      }
    } else {
      return getFallbackMarker();
    }
  }

  useEffectOnce(() => {
    of(null)
      .pipe(delay(1000))
      .subscribe(() => {
        setControlHeightInner();
        invalidateMapSize();
      });
  });

  // this should only run once, we assume that the number of markers won't change
  // during the lifecycle of the component, so we use the number of markers to execute this
  // side-effect only once.
  useEffect(() => {
    if (!props.markers || props.markers.length === 0 || props.selectedMarker) {
      return;
    }

    const leafletMarkers = [];
    const map = mapRef.current.leafletElement;

    map.eachLayer(layer => {
      if (!layer.getIcon) {
        return;
      }
      leafletMarkers.push(layer);
    });
    const group = new L.featureGroup(leafletMarkers);
    map.fitBounds(group.getBounds());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.markers]);

  let position = props.centerPosition;
  // we must comply with OSM/CARTO licensing
  // const attribution = '&copy; <a href=&quot;http://osm.org/copyright&quot;>OpenStreetMap</a> contributors';
  const attribution =
    'Map tiles by <a href="https://carto.com/">Carto</a>, under CC BY 3.0. Data by ' +
    "<a href=&quot;http://osm.org/copyright&quot;>OpenStreetMap</a> contributors, under ODbL.";
  // this is slower:
  //const tilesUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  // Carto watermarks raster tiles without a key (AHD-1285).
  const tilesUrl =
    "https://basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}.png?key=cb1_2rvy_1_0fc0d9b096e797124a0330a0";

  if (
    props.markers &&
    props.markers.length > 0 &&
    props.centralizeAroundMarkers
  ) {
    if (!props.selectedMarker) {
      const meanLat = _.meanBy(props.markers, marker => marker.latitude);
      const meanLong = _.meanBy(props.markers, marker => marker.longitude);
      position = [meanLat, meanLong];
    } else {
      const meanLat = _.find(
        props.markers,
        marker => marker.name === props.selectedMarker
      )?.latitude;
      const meanLong = _.find(
        props.markers,
        marker => marker.name === props.selectedMarker
      )?.longitude;
      position = [meanLat, meanLong];
    }
    //position = [props.markers[1].latitude, props.markers[1].longitude];
  }

  const onViewportChanged = viewport => {
    setMapZoom(viewport.zoom);
  };

  const classes = useStyles();

  return (
    <Map
      center={position}
      zoom={mapZoom}
      maxZoom={props.maxZoom}
      style={{ height: controlHeight }}
      ref={mapRef}
      className={classes.mapBody}
      onViewportChanged={onViewportChanged}
    >
      <AwesomeTileLayer
        url={tilesUrl}
        attribution={attribution}
        filter={[]}
        ref={awesomeTileLayerRef}
        edgeBufferTiles={props.edgeBufferTiles}
        useEdgeBuffer={props.useEdgeBuffer}
      />
      {renderMarkerGroups(props.markers, props.clusterMarkers)}
      <LoadingControl />
    </Map>
  );
}

MapControl.propTypes = {
  selectedMarker: PropTypes.string,
  panel: PropTypes.object,
  centerPosition: PropTypes.arrayOf(PropTypes.number),
  centralizeAroundMarkers: PropTypes.bool,
  startingZoom: PropTypes.number,
  clusterMarkers: PropTypes.bool,
  disableClusteringAtZoom: PropTypes.number,
  spiderfyOnMaxZoom: PropTypes.bool,
  useEdgeBuffer: PropTypes.bool,
  edgeBufferTiles: PropTypes.number,
  maxZoom: PropTypes.number,
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired,
      label: PropTypes.string,
      name: PropTypes.string,
      valueToDisplay: PropTypes.any,
      icon: PropTypes.func,
      zIndexOffset: PropTypes.number
    })
  )
};

MapControl.defaultProps = {
  panel: {},
  centerPosition: [51.505, -0.09],
  markers: [],
  startingZoom: 13,
  centralizeAroundMarkers: true,
  clusterMarkers: false,
  disableClusteringAtZoom: 10,
  spiderfyOnMaxZoom: false,
  useEdgeBuffer: true,
  edgeBufferTiles: 1,
  maxZoom: 20
};

export default React.memo(MapControl);
